"use client"

import { useEffect, useState } from "react"
import { Outlet, useParams, useLocation, useNavigate } from "react-router-dom"
import Companyheader from "../components/Companyheader"
import { getPublicCompanyAbout, getCompanyProfile, getUserProfile, getCompanyBySlug } from "../api/companyApi"
import { b2cGetProfile } from "../api/b2cUserApi"
import NotFound404 from "../pages/NotFound404"

const CompanyLayout = () => {
  const { companyName } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [companyInfo, setCompanyInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isCompanyAuth, setIsCompanyAuth] = useState(false)
  const [isB2CAuth, setIsB2CAuth] = useState(false)
  const [userProfile, setUserProfile] = useState(null)
  const [companyToken, setCompanyToken] = useState(null)
  const [b2cToken, setB2CBtoken] = useState(null)
  const [companyExists, setCompanyExists] = useState(true)
  const [validationLoading, setValidationLoading] = useState(true)

  const decodeJWT = (token) => {
    try {
      const base64Url = token.split(".")[1]
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
      return JSON.parse(window.atob(base64))
    } catch (e) {
      console.error("[v0] Error decoding JWT:", e)
      return null
    }
  }

  useEffect(() => {
    console.log("[v0] Initializing auth from localStorage")
    const storedCompanyToken = localStorage.getItem("companyAuthToken")
    const storedB2CToken = localStorage.getItem("authToken")

    console.log("[v0] Company token exists:", !!storedCompanyToken)
    console.log("[v0] B2C token exists:", !!storedB2CToken)

    if (storedCompanyToken) {
      setCompanyToken(storedCompanyToken)
      setIsCompanyAuth(true)
    }
    if (storedB2CToken) {
      setB2CBtoken(storedB2CToken)
      setIsB2CAuth(true)
    }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tokenFromUrl = params.get("token")

    if (tokenFromUrl) {
      console.log("[v0] Token found in URL, setting company auth as primary")
      localStorage.setItem("companyAuthToken", tokenFromUrl)
      setCompanyToken(tokenFromUrl)
      setIsCompanyAuth(true)

      localStorage.removeItem("authToken")
      setIsB2CAuth(false)
      setB2CBtoken(null)

      // Clean token parameter from URL
      params.delete("token")
      const newSearch = params.toString()
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : "")
      navigate(newPath, { replace: true })
    }
  }, [location.search, navigate])

  useEffect(() => {
    const handleAuthUpdate = () => {
      console.log("[v0] Auth update event triggered, reinitializing")
      // Re-read from localStorage
      const storedCompanyToken = localStorage.getItem("companyAuthToken")
      const storedB2CToken = localStorage.getItem("authToken")

      console.log("[v0] After update - Company token exists:", !!storedCompanyToken)
      console.log("[v0] After update - B2C token exists:", !!storedB2CToken)

      setIsCompanyAuth(!!storedCompanyToken)
      setIsB2CAuth(!!storedB2CToken)
      setCompanyToken(storedCompanyToken)
      setB2CBtoken(storedB2CToken)
    }

    // Listen to custom event from login pages
    window.addEventListener("authUpdate", handleAuthUpdate)
    // Listen to storage events from other tabs
    window.addEventListener("storage", handleAuthUpdate)

    return () => {
      window.removeEventListener("authUpdate", handleAuthUpdate)
      window.removeEventListener("storage", handleAuthUpdate)
    }
  }, [])

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Fetch company profile or user profile if company auth is active
        if (isCompanyAuth && companyToken) {
          console.log("[v0] Fetching profile with company token")
          const decoded = decodeJWT(companyToken)
          console.log("[v0] Decoded token payload:", decoded)
          if (decoded && (decoded.role === "user" || decoded.isSalesman)) {
            console.log("[v0] Token is user/salesman token, fetching user profile")
            const profileData = await getUserProfile(companyToken)
            console.log("[v0] Fetched user profileData:", profileData)
            setUserProfile(profileData?.data || profileData)
          } else {
            console.log("[v0] Token is company token, fetching company profile")
            const profileData = await getCompanyProfile(companyToken)
            console.log("[v0] Fetched company profileData:", profileData)
            setUserProfile(profileData?.data || profileData)
          }
          return
        }

        // Fetch B2C profile if B2C auth is active
        if (isB2CAuth && b2cToken) {
          console.log("[v0] Fetching B2C profile with B2C token")
          const profileData = await b2cGetProfile(b2cToken)
          setUserProfile(profileData?.data || profileData)
          return
        }
      } catch (error) {
        console.error("[v0] Error fetching user profile:", error)
        // Only log the error and keep user authenticated
        // User will see the logout button regardless of profile fetch status
      }
    }

    fetchUserProfile()
  }, [isCompanyAuth, isB2CAuth, companyToken, b2cToken])

  useEffect(() => {
    const validateCompanyExists = async () => {
      if (!companyName) return
      try {
        setValidationLoading(true)
        console.log("[v0] Validating company existence for:", companyName)
        const response = await getCompanyBySlug(companyName)
        const exists = response?.exists ?? true
        console.log("[v0] Company exists response:", response)
        console.log("[v0] Company exists flag:", exists)
        setCompanyExists(exists)
      } catch (error) {
        console.error("[v0] Error validating company:", error)
        setCompanyExists(false)
      } finally {
        setValidationLoading(false)
      }
    }

    validateCompanyExists()
  }, [companyName])

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyName) return
      try {
        setLoading(true)
        const data = await getPublicCompanyAbout(companyName)
        setCompanyInfo(data?.data || data)
      } catch (error) {
        console.error("[v0] Error fetching company data in layout:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchCompanyData()
  }, [companyName])

  const handleLogout = () => {
    // Check if user is a salesman before clearing token
    let isSalesman = false
    const token = localStorage.getItem("companyAuthToken")
    if (token) {
      const decoded = decodeJWT(token)
      isSalesman = decoded?.isSalesman === true
    }

    localStorage.removeItem("companyAuthToken")
    localStorage.removeItem("authToken")
    setIsCompanyAuth(false)
    setIsB2CAuth(false)
    setCompanyToken(null)
    setB2CBtoken(null)
    setUserProfile(null)

    if (isSalesman) {
      window.location.href = "/" // Navigate to system site
    }
  }

  if (!validationLoading && !companyExists) {
    return <NotFound404 />
  }

  if (validationLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Companyheader
        companyInfo={companyInfo}
        loading={loading}
        isCompanyAuth={isCompanyAuth}
        isB2CAuth={isB2CAuth}
        userProfile={userProfile}
        onLogout={handleLogout}
      />
      <Outlet context={{ companyInfo, loading, isCompanyAuth, isB2CAuth, userProfile }} />
    </>
  )
}

export default CompanyLayout
